import { Injectable, inject, signal, effect } from '@angular/core';
import { ServiceInventoryService, ServiceState } from '@brightside-web/desktop/data-access/service-inventory';
import { HomeFeedSection, HomeFeedService } from '@brightside-web/desktop/feature/home-feed';
import { Subject, filter, takeUntil, tap, take } from 'rxjs';
import { CreditInitialCardComponent } from './home-cards/credit-initial-card/credit-initial-card.component';
import { CreditPendingCardComponent } from './home-cards/credit-pending-card/credit-pending-card.component';
import { CreditCompleteCardComponent } from './home-cards/credit-complete-card/credit-complete-card.component';
import { CreditErrorCardComponent } from './home-cards/credit-error-card/credit-error-card.component';
import { CreditCompleteActionComponent } from './home-cards/credit-complete-action/credit-complete-action.component';
import { MobileStateService } from '@brightside-web/desktop/data-access/shared';
import { DevMenuService } from '@brightside-web/desktop/feature/dev-settings';
import { Environment } from '@micro-core/environment';

@Injectable({ providedIn: 'root' })
export class CreditScoreCardService {
  private _homeFeedService = inject(HomeFeedService);
  private _serviceInventory = inject(ServiceInventoryService);
  private _unsub$ = new Subject<void>();
  private _msService = inject(MobileStateService);
  private _devMenuService = inject(DevMenuService);
  private _env = inject(Environment);
  private _state: ServiceState;

  constructor() {
    this._serviceInventory
      .getService('credit')
      .pipe(
        filter((service) => !!service),
        tap((service) => {
          this._state = service.state;
          switch (service.state) {
            case ServiceState.INITIAL:
              this._msService.get().pipe(
                take(1),
                tap(ms => {
                  const onboardingTS = ms.onboardingComplete;
                  if (onboardingTS) {
                    const onboardingDate = new Date(onboardingTS);
                    const now = new Date();
                    const diffMs = now.getTime() - onboardingDate.getTime();
                    const diffDays = diffMs / (1000 * 60 * 60 * 24);
                    const isWithin90Days = diffDays <= 90 && diffDays >= 0;
                    if (isWithin90Days) {
                      this.registerCreditSetupActionCard();
                    }
                  }

                })
              ).subscribe();
              this.registerCreditScoreInitialCard();
              break;
            case ServiceState.PENDING:
              this.removeCreditSetupActionCard();
              this.registerCreditScorePendingCard();
              break;
            case ServiceState.ERROR:
              this.removeCreditSetupActionCard();
              this.registerCreditScoreErrorCard();
              break;
            case ServiceState.COMPLETE:
              this.removeCreditSetupActionCard();
              this.registerCreditScoreCompleteCard();
              break;
            case ServiceState.MAINTENANCE:
              break;
          }
        }),
        takeUntil(this._unsub$)
      )
      .subscribe();


    effect(()=>{
      const toggleCard = this._devMenuService.toggleDisplayCreditActionCard();
      if(!this._env.production && this._state === ServiceState.INITIAL) {
        if (toggleCard === true) {
          this.registerCreditSetupActionCard();
        } else if (toggleCard === false) {
          this.removeCreditSetupActionCard();
        }
      }
    }, {allowSignalWrites: true});

  }

  ngOnDestroy() {
    this._unsub$.next();
  }



  private registerCreditScoreInitialCard() {
    this._homeFeedService.removeHomeFeedCard('credit', HomeFeedSection.MYMONEYCOMPLETE);
    this._homeFeedService.registerHomeFeedCard({
      key: 'credit',
      component: 'CreditInitialCardComponent',
      componentRef: CreditInitialCardComponent,
      data: null,
      order: 3,
      section: HomeFeedSection.MYMONEYINITIAL,
    });
  }

  private registerCreditScorePendingCard() {
    this._homeFeedService.removeHomeFeedCard('credit', HomeFeedSection.MYMONEYINITIAL);
    this._homeFeedService.registerHomeFeedCard({
      key: 'credit',
      component: 'CreditPendingCardComponent',
      componentRef: CreditPendingCardComponent,
      data: null,
      order: 3,
      section: HomeFeedSection.MYMONEYCOMPLETE,
    });
  }

  private registerCreditScoreErrorCard() {
    this._homeFeedService.removeHomeFeedCard('credit', HomeFeedSection.MYMONEYINITIAL);
    this._homeFeedService.registerHomeFeedCard({
      key: 'credit',
      component: 'CreditErrorCardComponent',
      componentRef: CreditErrorCardComponent,
      data: null,
      order: 3,
      section: HomeFeedSection.MYMONEYCOMPLETE,
    });
  }

  private registerCreditScoreCompleteCard() {
    this._homeFeedService.removeHomeFeedCard('credit', HomeFeedSection.MYMONEYINITIAL);
    this._homeFeedService.registerHomeFeedCard({
      key: 'credit',
      component: 'CreditCompleteCardComponent',
      componentRef: CreditCompleteCardComponent,
      data: null,
      order: 3,
      section: HomeFeedSection.MYMONEYCOMPLETE,
    });
  }

  private registerCreditSetupActionCard() {
    this._homeFeedService.registerHomeFeedCard({
      key: 'credit-action',
      component: 'CreditCompleteActionComponent',
      componentRef: CreditCompleteActionComponent,
      data: null,
      order: 30,
      section: HomeFeedSection.ACTIONS,
    });
  }

  private removeCreditSetupActionCard() {
    this._homeFeedService.removeHomeFeedCard('credit-action', HomeFeedSection.ACTIONS);
  }

}
